import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Inbox, Loader2, Star, Quote, Trash2, Check, Bug, Lightbulb, Heart, MessageSquare, AlertTriangle, Sparkles } from 'lucide-react';

const SENTIMENT = { 1: { e: '😣', label: 'Very unhappy' }, 2: { e: '🙁', label: 'Unhappy' }, 3: { e: '😐', label: 'Neutral' }, 4: { e: '🙂', label: 'Happy' }, 5: { e: '😄', label: 'Very happy' } };

const TYPE_META = {
  bug: { label: 'Bug', icon: Bug, color: 'hsl(var(--destructive))' },
  idea: { label: 'Idea', icon: Lightbulb, color: 'hsl(var(--brand-honey))' },
  praise: { label: 'Praise', icon: Heart, color: 'hsl(var(--success))' },
  other: { label: 'Note', icon: MessageSquare, color: 'hsl(var(--muted-foreground))' },
};

export default function FeedbackInbox() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/feedback');
      setData(res.data);
    } catch (e) { if (e?.response?.status !== 403) console.debug('feedback load failed', e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleFeature = async (id) => {
    try { await api.patch(`/admin/feedback/${id}/feature`); load(); }
    catch (e) { toast.error('Could not update'); }
  };
  const markRead = async (id) => {
    try { await api.patch(`/admin/feedback/${id}/read`); load(); }
    catch (e) { console.debug('mark read failed', e); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/feedback/${id}`); toast.success('Removed'); load(); }
    catch (e) { toast.error('Could not remove'); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5 flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="feedback-inbox-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading feedback…
      </div>
    );
  }
  if (!data) return null;

  const items = data.items || [];

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5" data-testid="feedback-inbox">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'hsl(var(--accent))' }}><Inbox className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /></div>
        <div className="flex-1">
          <div className="flex items-center gap-2"><h3 className="font-display font-600">Feedback inbox</h3><Badge variant="secondary" className="text-[10px]">Owner</Badge>{data.unread > 0 && <Badge className="text-[10px]" style={{ background: 'hsl(var(--primary))' }} data-testid="feedback-unread-badge">{data.unread} new</Badge>}</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">What testers are saying. Feature the best quotes on your landing page.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-lg bg-[hsl(var(--surface-2))] p-2.5 text-center" data-testid="feedback-stat-total"><p className="font-display text-xl font-700">{data.count}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">total</p></div>
        <div className="rounded-lg bg-[hsl(var(--surface-2))] p-2.5 text-center" data-testid="feedback-stat-unread"><p className="font-display text-xl font-700" style={{ color: 'hsl(var(--primary))' }}>{data.unread}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">new</p></div>
        <div className="rounded-lg bg-[hsl(var(--surface-2))] p-2.5 text-center" data-testid="feedback-stat-featured"><p className="font-display text-xl font-700">{data.featured}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">featured</p></div>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))] py-6 text-center">No feedback yet — it'll show up here as testers send notes.</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((f) => (
            <div key={f.feedback_id} className="rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`feedback-item-${f.feedback_id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <span className="text-sm font-600">{f.name || 'Tester'}</span>
                <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{f.email}</span>
                {(() => { const m = TYPE_META[f.type] || TYPE_META.other; return (
                  <Badge variant="outline" className="text-[10px] gap-1" style={{ color: m.color, borderColor: m.color }} data-testid={`feedback-type-badge-${f.feedback_id}`}>
                    <m.icon className="h-3 w-3" /> {m.label}
                  </Badge>
                ); })()}
                {f.status !== 'read' && <Badge className="text-[10px]" style={{ background: 'hsl(var(--primary))' }}>New</Badge>}
                {f.urgent && <Badge className="text-[10px] gap-1 text-white" style={{ background: 'hsl(var(--destructive))' }} data-testid={`feedback-urgent-${f.feedback_id}`}><AlertTriangle className="h-3 w-3" /> Urgent</Badge>}
                {f.sentiment && <span className="text-[13px]" title={`Sentiment: ${(SENTIMENT[f.sentiment] || {}).label || ''}`} data-testid={`feedback-sentiment-${f.feedback_id}`}>{(SENTIMENT[f.sentiment] || {}).e}</span>}
                {f.ai_classified && <Badge variant="outline" className="text-[9px] gap-1 text-[hsl(var(--muted-foreground))]" title="Auto-triaged by Jev"><Sparkles className="h-2.5 w-2.5" /> AI</Badge>}
                {f.featured && <Badge variant="secondary" className="text-[10px] gap-1"><Quote className="h-3 w-3" /> Featured</Badge>}
                {f.rating ? (
                  <span className="flex items-center gap-0.5 ml-auto">
                    {Array.from({ length: f.rating }).map((_, i) => (
                      <Star key={i} className="h-3.5 w-3.5" style={{ color: 'hsl(var(--brand-honey))', fill: 'hsl(var(--brand-honey))' }} />
                    ))}
                  </span>
                ) : null}
              </div>
              <p className="text-sm leading-relaxed">{f.message}</p>
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" variant={f.featured ? 'default' : 'secondary'} className="rounded-lg h-8 text-xs gap-1.5 border border-[hsl(var(--border))]" style={f.featured ? { background: 'hsl(var(--primary))' } : {}} onClick={() => toggleFeature(f.feedback_id)} data-testid={`feedback-feature-${f.feedback_id}`}>
                  <Quote className="h-3.5 w-3.5" /> {f.featured ? 'Featured' : 'Feature'}
                </Button>
                {f.status !== 'read' && (
                  <Button size="sm" variant="ghost" className="rounded-lg h-8 text-xs gap-1.5" onClick={() => markRead(f.feedback_id)} data-testid={`feedback-read-${f.feedback_id}`}>
                    <Check className="h-3.5 w-3.5" /> Mark read
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="rounded-lg h-8 text-xs gap-1.5 ml-auto text-[hsl(var(--destructive))]" onClick={() => remove(f.feedback_id)} data-testid={`feedback-delete-${f.feedback_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
